/**
 * Two-click destructive confirm — the replacement for the old
 * ``window.prompt`` typed-confirm sentences.
 *
 * The dialog shows the operation summary (what the operator used to have
 * to type), optionally an embedded ``<CommandPreview />`` via
 * ``children``, and a red confirm button that stays disabled for
 * ``ARM_MS`` after open (a visible sweep animates the arming) so the
 * second click is always deliberate — a double-click can't carry over
 * from the button that opened the dialog. Esc and backdrop click cancel.
 *
 * Hand-rolled on the same fixed-overlay pattern as help/HelpPanel.tsx
 * (no Radix Dialog dependency). Callers use the ``useConfirm()`` hook:
 *
 *   const { confirm, confirmDialog } = useConfirm();
 *   ...
 *   if (!(await confirm({ title, summary }))) return;
 *   ...
 *   return <div>… {confirmDialog}</div>;
 */
import { AlertTriangle } from "lucide-react";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { cn } from "../lib/cn";
import { Button } from "./Button";

/** How long the confirm button stays disarmed after the dialog opens. */
const ARM_MS = 700;

export type ConfirmOptions = {
  /** Dialog heading, e.g. "Confirm deprovision_onu". */
  title: string;
  /** Operation summary — the old typed-confirm sentence. Multi-line
   *  strings render with line breaks preserved. */
  summary: string;
  /** Extra body content, e.g. a rendered ``<CommandPreview />``. */
  children?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
};

export function ConfirmDialog({
  title,
  summary,
  children,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  onConfirm,
  onCancel,
}: ConfirmOptions & { onConfirm: () => void; onCancel: () => void }) {
  // Arming: disabled for ARM_MS after open, with a sweep across the
  // button so the wait reads as intentional rather than broken. The
  // sweep flips on after first paint (same trick as HelpPanel's
  // slide-in) so the CSS transition actually plays.
  const [armed, setArmed] = useState(false);
  const [sweeping, setSweeping] = useState(false);
  useEffect(() => {
    const raf = requestAnimationFrame(() => setSweeping(true));
    const timer = setTimeout(() => setArmed(true), ARM_MS);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(timer);
    };
  }, []);

  // Esc to cancel.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onCancel();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 p-4"
      onClick={onCancel}
      role="alertdialog"
      aria-modal="true"
      aria-label={title}
    >
      <div
        className="flex w-full max-w-lg flex-col gap-3 rounded-lg bg-slate-900 p-4 shadow-border"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 shrink-0 text-red-400" />
          <h2 className="text-sm font-medium text-slate-200">{title}</h2>
        </div>
        <p className="whitespace-pre-line font-mono text-xs text-slate-300">
          {summary}
        </p>
        {children}
        <div className="flex items-center justify-end gap-2 pt-1">
          {/* Cancel takes initial focus so Enter never confirms. */}
          <Button variant="secondary" onClick={onCancel} autoFocus>
            {cancelLabel}
          </Button>
          <Button
            variant="danger"
            static
            disabled={!armed}
            onClick={onConfirm}
            className="relative overflow-hidden"
          >
            {!armed && (
              <span
                aria-hidden
                className={cn(
                  "absolute inset-0 origin-left bg-red-500/20",
                  "transition-transform ease-linear",
                  sweeping ? "scale-x-100" : "scale-x-0",
                )}
                style={{ transitionDuration: `${ARM_MS}ms` }}
              />
            )}
            <span className="relative">{confirmLabel}</span>
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * Promise-based confirm for imperative call sites. No provider needed —
 * the hook owns the open/close state; the caller just renders the
 * returned ``confirmDialog`` node anywhere in its tree (the dialog is a
 * fixed overlay, position doesn't matter).
 */
export function useConfirm(): {
  confirm: (opts: ConfirmOptions) => Promise<boolean>;
  confirmDialog: ReactNode;
} {
  const [opts, setOpts] = useState<ConfirmOptions | null>(null);
  const resolveRef = useRef<((ok: boolean) => void) | null>(null);

  const confirm = useCallback((next: ConfirmOptions) => {
    return new Promise<boolean>((resolve) => {
      // A second confirm() while one is open cancels the first.
      resolveRef.current?.(false);
      resolveRef.current = resolve;
      setOpts(next);
    });
  }, []);

  const settle = useCallback((ok: boolean) => {
    resolveRef.current?.(ok);
    resolveRef.current = null;
    setOpts(null);
  }, []);

  const confirmDialog = opts ? (
    <ConfirmDialog
      {...opts}
      onConfirm={() => settle(true)}
      onCancel={() => settle(false)}
    />
  ) : null;

  return { confirm, confirmDialog };
}
